import type { EmulatorRuntimeFailureReason } from './contracts';
import {
  EmulatorJsRuntimeHostError,
  type EmulatorJsHostLaunchConfiguration,
  type EmulatorJsRuntimeHost,
} from './emulatorjs-adapter';

const runtimeFrameChannel = 'retro-arcade/emulatorjs-frame/v1';

export const emulatorJsFrameSandboxTokens = [
  'allow-scripts',
  'allow-same-origin',
  'allow-pointer-lock',
] as const;

type RuntimeControlOperation =
  | 'pause'
  | 'resume'
  | 'restart'
  | 'set-volume'
  | 'export-native-save'
  | 'import-native-save'
  | 'capture-save-state'
  | 'restore-save-state'
  | 'set-cheat'
  | 'reset-cheats';

interface RuntimeFrameMessage {
  readonly channel: typeof runtimeFrameChannel;
  readonly instanceId: string;
  readonly type:
    'runtime-ready' | 'game-started' | 'runtime-error' | 'control-complete' | 'control-error';
  readonly requestId?: string;
  readonly operation?: RuntimeControlOperation;
  readonly reason?: EmulatorRuntimeFailureReason;
  readonly data?: ArrayBuffer | null;
}

interface PendingControl {
  readonly resolve: (data: ArrayBuffer | null) => void;
  readonly reject: (error: Error) => void;
  readonly timeoutId: number;
}

export interface BrowserEmulatorJsRuntimeHostOptions {
  readonly startupTimeoutMs?: number;
  readonly controlTimeoutMs?: number;
}

function isRuntimeFrameMessage(value: unknown): value is RuntimeFrameMessage {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<RuntimeFrameMessage>;
  return (
    candidate.channel === runtimeFrameChannel &&
    typeof candidate.instanceId === 'string' &&
    (candidate.type === 'runtime-ready' ||
      candidate.type === 'game-started' ||
      candidate.type === 'runtime-error' ||
      candidate.type === 'control-complete' ||
      candidate.type === 'control-error')
  );
}

function frameLaunchConfiguration(configuration: EmulatorJsHostLaunchConfiguration) {
  return {
    loaderUrl: configuration.loaderUrl,
    dataBaseUrl: configuration.dataBaseUrl,
    gameName: configuration.gameName,
    gameId: configuration.gameId,
    system: configuration.system,
    expectedCore: configuration.expectedCore,
    controlScheme: configuration.controlScheme,
    defaultControllers: configuration.defaultControllers,
    threads: configuration.threads,
  };
}

async function readRomBytes(gameUrl: string): Promise<ArrayBuffer> {
  try {
    const response = await fetch(gameUrl);
    if (!response.ok) {
      throw new Error('ROM source response was not successful.');
    }
    return await response.arrayBuffer();
  } catch {
    throw new EmulatorJsRuntimeHostError(
      'ROM_SOURCE_UNAVAILABLE',
      'The runtime host could not read the selected ROM source.',
    );
  }
}

export function createEmulatorJsFrameDocument(): string {
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Retro Arcade emulator runtime</title>
  <style>
    html, body, #game {
      width: 100%;
      height: 100%;
      margin: 0;
      background: #000;
      overflow: hidden;
      overscroll-behavior: none;
    }
    body { font-family: system-ui, sans-serif; outline: none; }
  </style>
</head>
<body tabindex="-1">
  <div id="game"></div>
  <script>
    (() => {
      const channel = '${runtimeFrameChannel}';
      let bridge = null;
      let instanceId = null;
      let runtimeRomUrl = null;

      const gameplayScrollKeys = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

      const isEditableTarget = (target) =>
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable === true;

      const focusRuntime = () => {
        try {
          document.body.focus({ preventScroll: true });
        } catch {
          document.body.focus();
        }
      };

      document.addEventListener('pointerdown', focusRuntime, true);
      document.addEventListener(
        'keydown',
        (event) => {
          if (!isEditableTarget(event.target) && gameplayScrollKeys.has(event.key)) {
            event.preventDefault();
          }
        },
        true,
      );

      const send = (type, extra = {}, transfer = []) => {
        if (!bridge || !instanceId) return;
        bridge.postMessage({ channel, instanceId, type, ...extra }, transfer);
      };

      const requireGameManager = (emulator) => {
        if (!emulator.gameManager) throw new Error('Game manager is unavailable.');
        return emulator.gameManager;
      };

      const toTransferableBuffer = (value) => {
        if (value === null || value === undefined) return null;
        const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
        return bytes.slice().buffer;
      };

      const importNativeSave = (manager, buffer) => {
        if (!(buffer instanceof ArrayBuffer) || buffer.byteLength === 0) {
          throw new Error('Invalid native-save payload.');
        }

        const path = manager.getSaveFilePath();
        if (typeof path !== 'string' || !path) throw new Error('Native-save path is unavailable.');

        const parts = path.split('/');
        let current = '';
        for (let index = 0; index < parts.length - 1; index += 1) {
          if (!parts[index]) continue;
          current += '/' + parts[index];
          if (!manager.FS.analyzePath(current).exists) manager.FS.mkdir(current);
        }

        if (manager.FS.analyzePath(path).exists) manager.FS.unlink(path);
        manager.FS.writeFile(path, new Uint8Array(buffer));
        manager.loadSaveFiles();
      };

      const applyControl = (message) => {
        const emulator = window.EJS_emulator;
        if (!emulator) throw new Error('EmulatorJS runtime is not ready for controls.');

        switch (message.operation) {
          case 'pause':
            emulator.pause();
            return null;
          case 'resume':
            emulator.play();
            return null;
          case 'restart':
            requireGameManager(emulator).restart();
            return null;
          case 'set-volume': {
            const volume = Number(message.value);
            if (!Number.isFinite(volume) || volume < 0 || volume > 1) {
              throw new Error('Invalid volume.');
            }
            emulator.volume = volume;
            emulator.setVolume(volume);
            return null;
          }
          case 'export-native-save': {
            const save = requireGameManager(emulator).getSaveFile();
            return toTransferableBuffer(save);
          }
          case 'import-native-save':
            importNativeSave(requireGameManager(emulator), message.data);
            return null;
          case 'capture-save-state': {
            const manager = requireGameManager(emulator);
            if (!manager.supportsStates()) throw new Error('Save states are unsupported.');
            return toTransferableBuffer(manager.getState());
          }
          case 'restore-save-state': {
            const manager = requireGameManager(emulator);
            if (!manager.supportsStates()) throw new Error('Save states are unsupported.');
            if (!(message.data instanceof ArrayBuffer) || message.data.byteLength === 0) {
              throw new Error('Invalid save-state payload.');
            }
            manager.loadState(new Uint8Array(message.data));
            return null;
          }
          case 'set-cheat': {
            if (!Number.isInteger(message.index) || message.index < 0 || message.index >= 64) {
              throw new Error('Invalid cheat index.');
            }
            if (typeof message.enabled !== 'boolean') throw new Error('Invalid cheat state.');
            if (typeof message.code !== 'string') throw new Error('Invalid cheat code.');
            const code = message.code.trim();
            if (!code || code.length > 4096) throw new Error('Invalid cheat code.');
            requireGameManager(emulator).setCheat(message.index, message.enabled, code);
            return null;
          }
          case 'reset-cheats':
            requireGameManager(emulator).resetCheat();
            return null;
          default:
            throw new Error('Unsupported runtime control.');
        }
      };

      const bindControlBridge = () => {
        bridge.onmessage = (event) => {
          const message = event.data;
          if (!message || message.channel !== channel || message.instanceId !== instanceId) return;
          if (message.type !== 'control' || typeof message.requestId !== 'string') return;

          const operation =
            message.operation === 'pause' ||
            message.operation === 'resume' ||
            message.operation === 'restart' ||
            message.operation === 'set-volume' ||
            message.operation === 'export-native-save' ||
            message.operation === 'import-native-save' ||
            message.operation === 'capture-save-state' ||
            message.operation === 'restore-save-state' ||
            message.operation === 'set-cheat' ||
            message.operation === 'reset-cheats'
              ? message.operation
              : null;

          if (!operation) return;

          try {
            const data = applyControl(message);
            const transfer = data instanceof ArrayBuffer ? [data] : [];
            send('control-complete', { requestId: message.requestId, operation, data }, transfer);
          } catch {
            send('control-error', {
              requestId: message.requestId,
              operation,
              reason: 'CONTROL_REJECTED',
            });
          }
        };
        bridge.start();
      };

      window.addEventListener('error', () => {
        if (bridge) send('runtime-error', { reason: 'RUNTIME_BOOTSTRAP_FAILED' });
      });

      window.addEventListener('unhandledrejection', () => {
        if (bridge) send('runtime-error', { reason: 'RUNTIME_BOOTSTRAP_FAILED' });
      });

      window.addEventListener(
        'message',
        (event) => {
          if (event.source !== parent) return;
          const message = event.data;
          if (!message || message.channel !== channel || message.type !== 'launch') return;
          if (typeof message.instanceId !== 'string' || !message.configuration) return;
          if (!(message.romBytes instanceof ArrayBuffer) || event.ports.length !== 1) return;

          bridge = event.ports[0];
          instanceId = message.instanceId;
          bindControlBridge();

          try {
            runtimeRomUrl = URL.createObjectURL(
              new Blob([message.romBytes], { type: 'application/octet-stream' }),
            );

            const configuration = message.configuration;
            window.EJS_player = '#game';
            window.EJS_gameName = configuration.gameName;
            window.EJS_biosUrl = '';
            window.EJS_gameUrl = runtimeRomUrl;
            window.EJS_core = configuration.system;
            window.EJS_controlScheme = configuration.controlScheme;
            window.EJS_defaultControls = configuration.defaultControllers;
            window.EJS_pathtodata = configuration.dataBaseUrl;
            window.EJS_gameID = configuration.gameId;
            window.EJS_startOnLoaded = true;
            window.EJS_volume = 1;
            window.EJS_threads = false;
            window.EJS_disableDatabases = true;
            window.EJS_disableLocalStorage = true;
            window.EJS_disableAutoLang = false;
            window.EJS_ready = () => send('runtime-ready');
            window.EJS_onGameStart = () => send('game-started');

            const loader = document.createElement('script');
            loader.src = configuration.loaderUrl;
            loader.async = true;
            loader.onerror = () => send('runtime-error', { reason: 'RUNTIME_SCRIPT_FAILED' });
            document.body.appendChild(loader);
          } catch {
            send('runtime-error', { reason: 'RUNTIME_BOOTSTRAP_FAILED' });
          }
        },
        { once: true },
      );

      window.addEventListener('pagehide', () => {
        if (runtimeRomUrl) URL.revokeObjectURL(runtimeRomUrl);
        bridge?.close();
      });
    })();
  </script>
</body>
</html>`;
}

export class BrowserEmulatorJsRuntimeHost implements EmulatorJsRuntimeHost {
  private readonly container: HTMLElement;
  private readonly startupTimeoutMs: number;
  private readonly controlTimeoutMs: number;
  private frame: HTMLIFrameElement | null = null;
  private instanceId: string | null = null;
  private port: MessagePort | null = null;
  private timeoutId: number | null = null;
  private readonly pendingControls = new Map<string, PendingControl>();

  constructor(container: HTMLElement, options: BrowserEmulatorJsRuntimeHostOptions = {}) {
    this.container = container;
    this.startupTimeoutMs = options.startupTimeoutMs ?? 60_000;
    this.controlTimeoutMs = options.controlTimeoutMs ?? 5_000;
  }

  async launch(configuration: EmulatorJsHostLaunchConfiguration): Promise<void> {
    this.destroy();
    const romBytes = await readRomBytes(configuration.gameUrl);

    const frame = document.createElement('iframe');
    const instanceId = window.crypto.randomUUID();

    frame.title = `Emulator runtime for ${configuration.gameName}`;
    frame.className = 'retro-arcade-emulator-frame';
    frame.tabIndex = 0;
    frame.allow = 'autoplay; fullscreen; gamepad';
    frame.referrerPolicy = 'no-referrer';
    for (const token of emulatorJsFrameSandboxTokens) frame.sandbox.add(token);
    frame.srcdoc = createEmulatorJsFrameDocument();
    this.frame = frame;
    this.instanceId = instanceId;

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const messageChannel = new MessageChannel();
      this.port = messageChannel.port1;

      const cleanupStartup = () => {
        if (this.timeoutId !== null) {
          window.clearTimeout(this.timeoutId);
          this.timeoutId = null;
        }
      };

      const settleSuccess = () => {
        if (settled) return;
        settled = true;
        cleanupStartup();
        resolve();
      };

      const settleFailure = (reason: EmulatorRuntimeFailureReason) => {
        if (settled) return;
        settled = true;
        cleanupStartup();
        reject(
          new EmulatorJsRuntimeHostError(
            reason,
            'Pinned EmulatorJS runtime did not start successfully.',
          ),
        );
      };

      this.port.onmessage = (event: MessageEvent<unknown>) => {
        if (!isRuntimeFrameMessage(event.data) || event.data.instanceId !== instanceId) return;

        if (event.data.type === 'game-started') {
          settleSuccess();
          return;
        }

        if (event.data.type === 'runtime-error' && !settled) {
          settleFailure(event.data.reason ?? 'RUNTIME_BOOTSTRAP_FAILED');
          return;
        }

        const requestId = event.data.requestId;
        if (
          (event.data.type === 'control-complete' || event.data.type === 'control-error') &&
          requestId
        ) {
          const pending = this.pendingControls.get(requestId);
          if (!pending) return;

          window.clearTimeout(pending.timeoutId);
          this.pendingControls.delete(requestId);

          if (event.data.type === 'control-complete') {
            pending.resolve(event.data.data ?? null);
          } else {
            pending.reject(
              new EmulatorJsRuntimeHostError(
                event.data.reason ?? 'CONTROL_REJECTED',
                'EmulatorJS rejected the runtime control.',
              ),
            );
          }
        }
      };
      this.port.start();

      frame.addEventListener(
        'load',
        () => {
          const contentWindow = frame.contentWindow;
          if (!contentWindow) {
            settleFailure('RUNTIME_BOOTSTRAP_FAILED');
            return;
          }

          contentWindow.postMessage(
            {
              channel: runtimeFrameChannel,
              type: 'launch',
              instanceId,
              configuration: frameLaunchConfiguration(configuration),
              romBytes,
            },
            '*',
            [messageChannel.port2, romBytes],
          );
        },
        { once: true },
      );

      this.timeoutId = window.setTimeout(
        () => settleFailure('RUNTIME_STARTUP_TIMEOUT'),
        this.startupTimeoutMs,
      );
      this.container.replaceChildren(frame);
    });
  }

  pause(): Promise<void> {
    return this.sendControl('pause').then(() => undefined);
  }

  resume(): Promise<void> {
    return this.sendControl('resume').then(() => undefined);
  }

  restart(): Promise<void> {
    return this.sendControl('restart').then(() => undefined);
  }

  setVolume(volume: number): Promise<void> {
    return this.sendControl('set-volume', volume).then(() => undefined);
  }

  async exportNativeSave(): Promise<Uint8Array | null> {
    const data = await this.sendControl('export-native-save');
    return data ? new Uint8Array(data) : null;
  }

  importNativeSave(data: Uint8Array): Promise<void> {
    const payload = data.slice().buffer;
    return this.sendControl('import-native-save', undefined, payload).then(() => undefined);
  }

  async captureSaveState(): Promise<Uint8Array> {
    const data = await this.sendControl('capture-save-state');
    if (!data) {
      throw new EmulatorJsRuntimeHostError(
        'PERSISTENCE_PAYLOAD_INVALID',
        'Runtime returned no save-state payload.',
      );
    }
    return new Uint8Array(data);
  }

  restoreSaveState(data: Uint8Array): Promise<void> {
    const payload = data.slice().buffer;
    return this.sendControl('restore-save-state', undefined, payload).then(() => undefined);
  }

  setCheat(index: number, enabled: boolean, code: string): Promise<void> {
    return this.sendControl('set-cheat', undefined, undefined, { index, enabled, code }).then(
      () => undefined,
    );
  }

  resetCheats(): Promise<void> {
    return this.sendControl('reset-cheats').then(() => undefined);
  }

  async requestFullscreen(): Promise<void> {
    const frame = this.requireFrame();
    if (!frame.requestFullscreen) {
      throw new EmulatorJsRuntimeHostError(
        'FULLSCREEN_UNAVAILABLE',
        'Fullscreen API is unavailable for the emulator frame.',
      );
    }

    try {
      await frame.requestFullscreen();
    } catch {
      throw new EmulatorJsRuntimeHostError(
        'FULLSCREEN_REJECTED',
        'The browser rejected the fullscreen request.',
      );
    }
  }

  destroy(): void {
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    for (const pending of this.pendingControls.values()) {
      window.clearTimeout(pending.timeoutId);
      pending.reject(
        new EmulatorJsRuntimeHostError(
          'CONTROL_REJECTED',
          'Runtime host was destroyed before the control completed.',
        ),
      );
    }
    this.pendingControls.clear();

    if (this.port) {
      this.port.onmessage = null;
      this.port.close();
      this.port = null;
    }

    this.frame?.remove();
    this.frame = null;
    this.instanceId = null;
  }

  private sendControl(
    operation: RuntimeControlOperation,
    value?: number,
    data?: ArrayBuffer,
    cheat?: { readonly index: number; readonly enabled: boolean; readonly code: string },
  ): Promise<ArrayBuffer | null> {
    const port = this.port;
    const instanceId = this.instanceId;
    if (!instanceId || !port) {
      return Promise.reject(
        new EmulatorJsRuntimeHostError(
          'CONTROL_REJECTED',
          'Runtime control bridge is unavailable.',
        ),
      );
    }

    const requestId = window.crypto.randomUUID();

    return new Promise<ArrayBuffer | null>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        this.pendingControls.delete(requestId);
        reject(
          new EmulatorJsRuntimeHostError(
            'CONTROL_TIMEOUT',
            'Runtime control did not acknowledge before the timeout.',
          ),
        );
      }, this.controlTimeoutMs);

      this.pendingControls.set(requestId, { resolve, reject, timeoutId });
      const message = {
        channel: runtimeFrameChannel,
        type: 'control',
        instanceId,
        requestId,
        operation,
        ...(value === undefined ? {} : { value }),
        ...(data === undefined ? {} : { data }),
        ...(cheat ?? {}),
      };
      if (data) {
        port.postMessage(message, [data]);
      } else {
        port.postMessage(message);
      }
    });
  }

  private requireFrame(): HTMLIFrameElement {
    if (!this.frame) {
      throw new EmulatorJsRuntimeHostError('CONTROL_REJECTED', 'Runtime frame is unavailable.');
    }
    return this.frame;
  }
}
